import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { saveAs } from 'file-saver';
import { BACKEND_URL } from '../supabaseClient'; 
import { getRandomFact } from '../utils/techFacts'; 

// --- NEW ENGINE IMPORTS ---
import ToolWorkspace from './ToolEngine/ToolWorkspace/ToolWorkspace';
import ToolCanvas from './ToolEngine/ToolCanvas/ToolCanvas';
import ControlPanel from './ToolEngine/ControlPanel/ControlPanel';
import ImageUploader from './ToolEngine/Upload/ImageUploader';

export default function AiToolWrapper({ 
  title, 
  apiEndpoint, 
  SettingsFormComponent, 
  session, 
  actionName = "Generate", 
  refreshProfile,
  layoutVariant
}) {
  
  const navigate = useNavigate();
  
  // --- STATE ---
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [loadingState, setLoadingState] = useState('idle'); 
  const [error, setError] = useState(null);
  const [toolSettings, setToolSettings] = useState({});
  const [currentFact, setCurrentFact] = useState(""); 
  const [userProfile, setUserProfile] = useState(null);

  // --- LOGIC: INITIAL LOAD ---
  useEffect(() => {
    if (refreshProfile) {
        refreshProfile().then(data => setUserProfile(data));
    }
  }, [refreshProfile]);

  // --- LOGIC: TECH FACTS ---
  useEffect(() => {
    if (loadingState === 'processing') {
      setCurrentFact(getRandomFact());
    }
  }, [loadingState]);

  // --- LOGIC: UPLOAD HANDLER ---
  const handleUploadSuccess = async (fileOrUrl) => {
    setError(null);
    setProcessedImageUrl(null); 
    setToolSettings({}); 

    if (fileOrUrl instanceof File || fileOrUrl instanceof Blob) {
        const previewUrl = URL.createObjectURL(fileOrUrl);
        setUploadedImageUrl(previewUrl);
        setSelectedFile(fileOrUrl);
    } 
    else if (typeof fileOrUrl === 'string') {
        setUploadedImageUrl(fileOrUrl);
        try {
            const res = await fetch(fileOrUrl);
            const blob = await res.blob();
            const fileObj = new File([blob], "image.png", { type: blob.type || "image/png" });
            setSelectedFile(fileObj);
        } catch (err) {
            console.warn("Auto-recovery failed:", err);
        }
    }
  };

  const clearUpload = () => {
    if (uploadedImageUrl && uploadedImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(uploadedImageUrl);
    }
    setUploadedImageUrl(null);
    setSelectedFile(null);
    setProcessedImageUrl(null);
    setError(null);
    setToolSettings({});
  }

  // --- LOGIC: SUBMIT ---
  const handleSubmit = async () => {
    if (!uploadedImageUrl) return setError("Please upload a valid image first.");
    if (!session?.access_token) return setError("Authentication error. Please log in again.");

    // Limit Check
    if (userProfile) {
        const tier = (userProfile.subscription_tier || 'starter').toLowerCase();
        const used = userProfile.ai_tools_daily_usage || 0;
        if (!['lite', 'pro', 'agency'].includes(tier) && used >= 10) {
             toast.error("Daily limit reached (10/10). Upgrade for unlimited.");
             return;
        }
    }

    // Safety: Ensure File Object
    let fileToUpload = selectedFile;
    if (!fileToUpload && uploadedImageUrl) {
        try {
            const res = await fetch(uploadedImageUrl);
            const blob = await res.blob();
            fileToUpload = new File([blob], "upload.png", { type: blob.type || "image/png" });
        } catch (e) {
            return setError("Image data is missing.");
        }
    }

    setLoadingState('processing');
    setError(null);
    setProcessedImageUrl(null); 
    const toastId = toast.loading("Processing image...");

    try {
      const formData = new FormData();
      formData.append('image', fileToUpload);
      
      Object.keys(toolSettings).forEach(key => {
          if (toolSettings[key] !== null && toolSettings[key] !== undefined) {
              formData.append(key, toolSettings[key]);
          }
      });

      const finalUrl = apiEndpoint.startsWith('http') ? apiEndpoint : `${BACKEND_URL}${apiEndpoint}`;

      const response = await fetch(finalUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: "Server Error" }));
        throw new Error(errData.error || "Processing failed");
      }

      const data = await response.json();
      setProcessedImageUrl(data.outputUrl);
      toast.success("Done!", { id: toastId });

      if (refreshProfile) refreshProfile().then(d => setUserProfile(d));

    } catch (err) {
      toast.dismiss(toastId);
      console.error(err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoadingState('idle');
    }
  };

  // --- LOGIC: DOWNLOAD ---
  const handleDownload = async () => {
    if (!processedImageUrl) return;
    setLoadingState('downloading');
    const toastId = toast.loading('Downloading...');
    try {
        const response = await fetch(processedImageUrl);
        const blob = await response.blob();
        saveAs(blob, 'fullmockup-result.png');
        toast.success('Saved!', { id: toastId });
    } catch (err) {
        toast.error('Download failed', { id: toastId });
    } finally {
        setLoadingState('idle');
    }
  };

  // =========================================================================
  // ★ RENDER LOGIC ★
  // =========================================================================

  // 1. Prepare Sidebar Content
  const sidebarContent = (
    <ControlPanel 
        title={title} // ★ KEEP ORIGINAL TITLE (e.g. "Free Background Remover")
        isLoading={loadingState === 'processing' || loadingState === 'uploading'}
        disabled={!uploadedImageUrl}
        actionLabel={uploadedImageUrl ? actionName : null} 
        onAction={handleSubmit}
    >
        {/* CASE A: No Image -> Show Uploader Here */}
        {!uploadedImageUrl && (
            <ImageUploader onUpload={handleUploadSuccess} />
        )}

        {/* CASE B: Image Selected -> NEW SIDE-BY-SIDE LAYOUT */}
        {uploadedImageUrl && (
            <div className="preview-box-container">
                {/* The Label */}
                <span className="preview-label-text">Preview Image</span>
                
                {/* The Row: Image + Button */}
                <div className="preview-row">
                     <img 
                        src={uploadedImageUrl} 
                        alt="Preview" 
                        className="preview-thumbnail"
                     />
                     <button onClick={clearUpload} className="btn-change-image-small">
                        Change
                     </button>
                </div>
            </div>
        )}

        {/* Inject Settings (Best For, etc.) */}
        {SettingsFormComponent && (
            <SettingsFormComponent 
                settings={toolSettings} 
                onChange={setToolSettings} 
                inputImageUrl={uploadedImageUrl}
                inputFile={selectedFile}
            />
        )}
        
        {/* Error Message */}
        {error && <div className="tool-error-msg">{error}</div>}
    </ControlPanel>
  );

  // 2. Prepare Canvas Content
  const canvasContent = (
    <ToolCanvas
        imageSrc={processedImageUrl}
        originalSrc={uploadedImageUrl}
        isLoading={loadingState === 'processing'}
        loadingMessage={currentFact}
        isEmpty={!uploadedImageUrl}
        onDownload={handleDownload}

        layoutMode={layoutVariant || 'slider'}
        onUpload={handleUploadSuccess}
        
        // We pass a simple message here because the uploader is now in the sidebar
        uploadContent={
            <div style={{textAlign: 'center', opacity: 0.6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'}}>
                <p style={{fontSize: '1.1rem', fontWeight: 500, color: 'var(--text-light)'}}>
                    Upload image to get started
                </p>
                <div style={{fontSize: '2rem'}}>👈</div>
            </div>
        }
    />
  );

  return (
    <ToolWorkspace
        title={title}
        sidebarContent={sidebarContent}
        canvasContent={canvasContent}
    />
  );
}